import React, { memo, ReactNode } from 'react';
import { Field, Form, withFormik } from 'formik';
import * as Yup from 'yup';
import {
    Grid,
    Alert,
    FormControl,
    MenuItem,
    Slider,
    FormLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Select as MuiSelect } from 'formik-mui';
import { TextField } from '../formik/text-field';
import PersianDatePicker from 'components/formik/date-picker/date-picker';
import { CitySelector } from 'components/formik';
import PersianDatePickerRange from 'components/formik/date-picker/date-picker-range';
import PersianDatePickerTime from 'components/formik/date-picker/date-picker-time';
import { searchFieldComponents } from 'app/search-field-registry';
import { GridSize } from '@mui/material/Grid';

const FormTemplate = ({
    searchJson,
    setSearch,
    onClose,
    values,
    setValues,
    isValid,
    submitForm,
    setSubmitting,
    isSubmitting,
}: any): JSX.Element => {
    const handleOnSubmit = async () => {
        submitForm();

        if (!isValid) return;

        if (isSubmitting) submitForm();

        if (Object.keys(values)?.length > 0) {
            let searchParams = Object.entries(values)
                .filter(([key, value]) => {
                    const fieldMeta = searchJson.fields?.find(
                        (f: SearchField) => f.name === key,
                    );

                    const isSelect =
                        fieldMeta?.component === 'Select' ||
                        fieldMeta?.component === 'PSP';

                    return (
                        value !== '' &&
                        value !== undefined &&
                        !(isSelect && value === 'all')
                    );
                })
                .reduce((acc: any, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});

            setSearch(searchParams);

            onClose();
        }

        setSubmitting(false);
    };

    const handleFieldChange = (fieldName: string) => (newValue: any) => {
        setValues({
            ...values,
            [fieldName]: newValue,
        });
    };

    return (
        <>
            <Alert variant="outlined" severity="info" sx={{ mb: 3 }}>
                لطفا برای جستجو حداقل یکی از فیلد های زیر را تکمیل نمایید.
            </Alert>

            <Form
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleOnSubmit();
                    }
                }}>
                <Grid container spacing={2}>
                    {searchJson?.fields?.map(
                        (field: SearchField, index: number) => {
                            return (
                                <Grid
                                    item
                                    xs={12}
                                    sm={field?.gridSize ? field.gridSize : 12}
                                    key={field.name || index}>
                                    <Field
                                        {...field}
                                        component={ComponentTypeSelector(
                                            field.component,
                                        )}
                                        onChange={handleFieldChange(
                                            field.name,
                                        )}>
                                        {(field.options?.length
                                            ? [
                                                  {
                                                      label: 'همه',
                                                      value: 'all',
                                                  },
                                                  ...field.options,
                                              ]
                                            : field.emptyOptionLabel
                                              ? [
                                                    {
                                                        label: field.emptyOptionLabel,
                                                        value: 'all',
                                                        disabled: true,
                                                    },
                                                ]
                                              : []
                                        ).map(
                                            (
                                                menu: SearchOption,
                                                index: number,
                                            ) => (
                                                <MenuItem
                                                    key={`${menu.value}-${index}`}
                                                    value={menu.value}
                                                    disabled={menu.disabled}>
                                                    {menu.label}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Field>
                                </Grid>
                            );
                        },
                    )}
                </Grid>

                <LoadingButton
                    fullWidth
                    variant="contained"
                    loading={isSubmitting}
                    onClick={handleOnSubmit}
                    sx={{ mt: 2 }}>
                    جستجو
                </LoadingButton>
            </Form>
        </>
    );
};

const SearchForm = withFormik({
    mapPropsToValues: ({ searchJson, search }: any) => {
        return searchJson?.fields.reduce(
            (acc: SearchValues, curr: SearchField) => {
                acc[curr.name] = search?.[curr.name] ?? '';
                return acc;
            },
            {},
        );
    },
    validationSchema: ({ searchJson }: { searchJson: SearchJsonObject }) => {
        const noSchema: Record<string, Yup.StringSchema> = {};

        searchJson?.fields?.forEach((field) => {
            noSchema[field?.name] = Yup.string().optional();
        });

        return searchJson?.validation
            ? searchJson.validation
            : Yup.object().shape(noSchema);
    },
    enableReinitialize: true,
    handleSubmit: (values, { setSubmitting }) => {
        setSubmitting(false);
    },
})(FormTemplate);

export default SearchForm;

const Select = memo((props: any) => {
    const fieldName = props?.field?.name;
    const fieldValue = props?.field?.value;
    const value =
        fieldValue === '' || fieldValue === undefined || fieldValue === null
            ? 'all'
            : fieldValue;

    const handleChange = (event: any) => {
        const nextValue = event.target.value;
        props?.form?.setFieldValue(fieldName, nextValue, false);
        props?.onChange?.(nextValue);
    };

    return (
        <FormControl fullWidth>
            <MuiSelect {...props} value={value} onChange={handleChange} />
        </FormControl>
    );
});

const SliderComponent = memo((props: any) => {
    const handleChange = (event: Event, newValue: number | number[]) => {
        props?.onChange(newValue as number | number[]);
    };

    return (
        <FormControl fullWidth>
            <FormLabel sx={{ fontSize: 12, fontWeight: 100 }}>
                {props?.label}
            </FormLabel>

            <Slider
                name={props?.name}
                value={props?.field?.value || (props?.range ? [0, 0] : 0)}
                onChange={handleChange}
                valueLabelDisplay="auto"
                min={props?.rangeValue ? props?.rangeValue[0] : 0}
                max={props?.rangeValue ? props?.rangeValue[1] : 100}
            />
        </FormControl>
    );
});

const ComponentTypeSelector = (component: SearchComponent) => {
    switch (component) {
        case 'Text':
            return TextField;
        case 'Select':
            return Select;
        case 'Date':
            return PersianDatePicker;
        case 'DateRange':
            return PersianDatePickerRange;
        case 'DateTime':
            return PersianDatePickerTime;
        case 'BankAccount':
        case 'Bank':
        case 'PSP':
            return searchFieldComponents[component];
        case 'City':
            return CitySelector;
        case 'Slider':
            return SliderComponent;
        default:
            return component;
    }
};

type SearchOption = {
    label: ReactNode;
    value: any;
    disabled?: boolean;
};

type SearchComponent =
    | React.ElementType
    | 'Text'
    | 'Select'
    | 'Date'
    | 'DateRange'
    | 'DateTime'
    | 'Slider'
    | 'BankAccount'
    | 'Bank'
    | 'City'
    | 'PSP';

type SearchField = {
    name: string;
    label: string;
    component: SearchComponent;
    icon?: ReactNode;
    secondIcon?: ReactNode;
    ltr?: 'true' | 'false';
    maxLength?: number;
    range?: boolean;
    rangeValue?: [number, number];
    issearch?: 'true' | 'false';
    options?: SearchOption[];
    gridSize?: GridSize;
    startOnDay?: boolean;
    customtype?: 'number';
    inputProps?: Record<string, any>;
    formatThousands?: boolean;
    numericOnly?: boolean;
    inputSanitizer?: (value: string) => string;
    readOnlyInput?: boolean;
    disableAutoFill?: boolean;
    emptyOptionLabel?: string;
    disabled?: boolean;
};

type SearchValues = {
    [key: string]: string;
};

export type SearchJsonObject = {
    fields: SearchField[];
    validation?: Yup.ObjectSchema<any>;
};
